import { P2pRemoteMedia, SfuRemoteMedia } from '@/RoomContext'
import { MediaDeviceError } from '@/services/MediaDevices'
import { MusePeer } from '@/services/RoomContextMusePeer'
import { cn } from '@/utlis/cn'
import { LocalTrack, RemoteTrack } from 'livekit-client'
import { VideoHTMLAttributes, useRef, useState, useEffect, memo } from 'react'

type VideoMedia = SfuRemoteMedia | P2pRemoteMedia | LocalTrack | MediaDeviceError | undefined

export class VideoSource {
    constructor(
        public user: MusePeer | 'me',
        public kind: 'camera' | 'screen',
        public media: VideoMedia = undefined,
        public videoProcessor?: string | null,
        public mirrored: boolean = false
    ) {}

    get id(): string {
        const p1 = this.user === 'me' ? 'me' : this.user.sessionId
        const p2 = this.kind
        let p3 = ''
        if (this.media instanceof LocalTrack) {
            p3 = this.media.id
        } else if (this.media instanceof MediaDeviceError) {
            p3 = this.media.name
        } else if (this.media === undefined) {
            p3 = 'undefined'
        } else {
            p3 = this.media.stream.id
        }

        return `${p1}-${p2}-${p3}`
    }

    get isMe(): boolean {
        return this.user === 'me'
    }

    get peer(): MusePeer | undefined {
        return this.user === 'me' ? undefined : this.user
    }

    get hasVideoStream(): boolean {
        if (this.media instanceof LocalTrack) {
            return true
        }
        if (this.media instanceof MediaDeviceError) {
            return false
        }
        return !!this.media
    }
}

type VideoProps = VideoHTMLAttributes<HTMLVideoElement> & {
    videoSource: VideoSource
    tabIndex?: number
    ignoreAspectRatio?: boolean
    className?: string
    hide?: boolean
}

export const Video2 = memo(
    ({ videoSource, tabIndex = undefined, ignoreAspectRatio, className, hide, ...props }: VideoProps) => {
        const videoRef = useRef<HTMLVideoElement>(null)
        const [defaultAspectRatio, setDefaultAspectRatio] = useState<string | undefined>()

        useEffect(() => {
            const videoNode = videoRef.current
            if (!videoSource || !videoNode) {
                return
            }
            if (videoSource.media instanceof LocalTrack) {
                videoSource.media.attach(videoNode)
                const aspectRatio = videoSource.media.mediaStreamTrack.getSettings().aspectRatio
                videoSource.media.mediaStreamTrack.muted
                setDefaultAspectRatio(aspectRatio ? `${aspectRatio}/1` : '16/9')
            } else if (videoSource.media instanceof MediaDeviceError) {
            } else if (videoSource.media) {
                if (videoSource.media instanceof RemoteTrack) {
                    videoSource.media.attach(videoNode)
                } else {
                    videoNode.srcObject = videoSource.media.stream
                }
                const track = videoSource.media.stream.getVideoTracks()[0] || null
                const aspectRatio = track ? track.getSettings().aspectRatio : null
                setDefaultAspectRatio(aspectRatio ? `${aspectRatio}/1` : '16/9')
            } else {
                console.warn('No srcObject or localTrack provided')
            }

            videoNode.setAttribute('autoplay', '')
            videoNode.setAttribute('muted', '')
            videoNode.setAttribute('playsinline', '')

            return () => {
                if (videoNode) {
                    if (videoSource.media instanceof LocalTrack) {
                        videoSource.media.detach(videoNode)
                    } else if (videoSource.media instanceof RemoteTrack) {
                        videoSource.media.detach(videoNode)
                    }
                    videoNode.srcObject = null
                }
            }
        }, [videoSource])

        if (!videoSource?.media || videoSource.media instanceof MediaDeviceError || hide) {
            return <div className={cn('bg-white/10 backdrop-blur-[50px]', className)}></div>
        }

        return (
            <video
                ref={videoRef}
                tabIndex={tabIndex}
                className={cn(
                    videoSource.mirrored ? '-scale-x-100' : undefined,
                    ignoreAspectRatio ? undefined : `aspect-[${defaultAspectRatio}]`,
                    className
                )}
                {...props}
            />
        )
    },
    (prev, next) => {
        return (
            prev.hide === next.hide &&
            prev.videoSource?.id === next.videoSource?.id &&
            prev.videoSource?.media === next.videoSource?.media &&
            prev.videoSource?.mirrored === next.videoSource?.mirrored &&
            prev.videoSource?.videoProcessor === next.videoSource?.videoProcessor &&
            prev.className === next.className &&
            prev.ignoreAspectRatio === next.ignoreAspectRatio
        )
    }
)
